// RV-1 — ROLLOVER tier configuration (UI source of truth).
// Edge-fn mirror lives in supabase/functions/_shared/rollover-tier-config.ts.
// Both files MUST stay in sync. Tests in src/rollover/__tests__/tierConfig.test.ts assert agreement.

export type RolloverTier = "2x" | "3x" | "4x" | "5x" | "6x";

export interface RolloverTierSpec {
  legs_min: number;
  legs_max: number;
  combined_odds_min: number;
  combined_odds_max: number;
  combined_odds_target: number;
  confidence_threshold: number;
  seeded_win_rate: number;
  adversarial_required: boolean;
  adversarial_trigger_score: number;
}

export const TIER_CONFIG: Record<RolloverTier, RolloverTierSpec> = {
  "2x": {
    legs_min: 1,
    legs_max: 12,
    combined_odds_min: 2.4,
    combined_odds_max: 2.8,
    combined_odds_target: 2.6,
    confidence_threshold: 45,
    seeded_win_rate: 0.68,
    adversarial_required: false,
    adversarial_trigger_score: 68,
  },
  "3x": {
    legs_min: 1,
    legs_max: 12,
    combined_odds_min: 3.4,
    combined_odds_max: 3.8,
    combined_odds_target: 3.6,
    confidence_threshold: 42,
    seeded_win_rate: 0.55,
    adversarial_required: false,
    adversarial_trigger_score: 68,
  },
  "4x": {
    legs_min: 1,
    legs_max: 12,
    combined_odds_min: 4.4,
    combined_odds_max: 4.8,
    combined_odds_target: 4.6,
    confidence_threshold: 40,
    seeded_win_rate: 0.48,
    adversarial_required: true,
    adversarial_trigger_score: 68,
  },
  "5x": {
    legs_min: 1,
    legs_max: 12,
    combined_odds_min: 5.4,
    combined_odds_max: 5.8,
    combined_odds_target: 5.6,
    confidence_threshold: 38,
    seeded_win_rate: 0.41,
    adversarial_required: true,
    adversarial_trigger_score: 68,
  },
  "6x": {
    legs_min: 1,
    legs_max: 12,
    combined_odds_min: 6.4,
    combined_odds_max: 6.8,
    combined_odds_target: 6.6,
    confidence_threshold: 38,
    seeded_win_rate: 0.36,
    adversarial_required: true,
    adversarial_trigger_score: 68,
  },
};

export const ROLLOVER_TIERS: RolloverTier[] = ["2x", "3x", "4x", "5x", "6x"];

export function getTierSpec(tier: RolloverTier): RolloverTierSpec {
  return TIER_CONFIG[tier];
}

export function isLegCountValid(tier: RolloverTier, legCount: number): boolean {
  const spec = TIER_CONFIG[tier];
  return legCount >= spec.legs_min && legCount <= spec.legs_max;
}

export function isCombinedOddsInRange(tier: RolloverTier, combinedOdds: number): boolean {
  const spec = TIER_CONFIG[tier];
  return combinedOdds >= spec.combined_odds_min && combinedOdds <= spec.combined_odds_max;
}

export function requiresAdversarial(tier: RolloverTier, minLegConfidence: number): boolean {
  const spec = TIER_CONFIG[tier];
  if (spec.adversarial_required) return true;
  return minLegConfidence < spec.adversarial_trigger_score;
}
